import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iniciPeriode } from '@/api/informes';
import type { NomIcona } from '@/components/Icona';

export type Periode = 'SETMANA' | 'MES' | 'TRIMESTRE';
export const PERIODES: Periode[] = ['SETMANA', 'MES', 'TRIMESTRE'];

export type FiltreAuditoria =
  'TOTS' | 'PERMISOS' | 'CONFIGURACIO' | 'USUARIS' | 'GUARDIES' | 'DOCUMENTACIO' | 'ACTIVACIONS';

export const FILTRES_AUDITORIA: Record<FiltreAuditoria, string | null> = {
  TOTS: null,
  PERMISOS: 'rol.',
  CONFIGURACIO: 'configuracio',
  USUARIS: 'usuari.',
  GUARDIES: 'torn.',
  DOCUMENTACIO: 'document.',
  ACTIVACIONS: 'activacio',
};

export const RESULTATS_AUDITORIA = ['OK', 'ERROR', 'DENEGAT'] as const;

export const CANALS_MONITORATGE = ['PUSH', 'PUSH_CRITICA', 'EMAIL', 'TRUCADA'] as const;

export interface Patro {
  clau: 'MATEIX_SERVEI' | 'MATEIXA_ZONA' | 'CONCENTRACIO';
  params: Record<string, string | number>;
}

export function detectaPatrons(
  recurrents: { servei?: string | null; zona?: string | null; total: number }[],
): Patro[] {
  const patrons: Patro[] = [];
  const perServei = new Map<string, number>();
  const perZona = new Map<string, number>();
  recurrents.forEach((r) => {
    if (r.servei) perServei.set(r.servei, (perServei.get(r.servei) ?? 0) + r.total);
    if (r.zona) perZona.set(r.zona, (perZona.get(r.zona) ?? 0) + r.total);
  });
  [...perServei.entries()]
    .filter(([, n]) => n >= 3)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .forEach(([servei, n]) => patrons.push({ clau: 'MATEIX_SERVEI', params: { servei, n } }));
  [...perZona.entries()]
    .filter(([, n]) => n >= 3)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .forEach(([zona, n]) => patrons.push({ clau: 'MATEIXA_ZONA', params: { zona, n } }));
  const total = recurrents.reduce((s, r) => s + r.total, 0);
  const top = recurrents[0];
  if (top && total > 0 && top.total / total >= 0.5 && recurrents.length > 1) {
    patrons.push({
      clau: 'CONCENTRACIO',
      params: { servei: top.servei ?? '—', pct: Math.round((top.total / total) * 100) },
    });
  }
  return patrons;
}

export function usePeriode(inicial: Periode = 'MES') {
  const [periode, setPeriode] = useState<Periode>(inicial);
  const rang = useMemo(() => {
    const ara = new Date();
    const desde = iniciPeriode(periode, ara);
    const anterior = iniciPeriode(periode, desde);
    return {
      desde: desde.toISOString(),
      fins: ara.toISOString(),
      anteriorDesde: anterior.toISOString(),
    };
  }, [periode]);
  return { periode, setPeriode, ...rang };
}

export function useTemps() {
  const { t } = useTranslation('informes');
  return (seg?: number | null) => {
    if (seg == null) return '—';
    if (seg < 60) return t('comu.segons', { n: Math.round(seg) });
    if (seg < 3600) return t('comu.minuts', { n: Math.round(seg / 60) });
    return t('comu.hores', { n: Math.round((seg / 3600) * 10) / 10 });
  };
}

export function useEtiquetaEvent() {
  const { t } = useTranslation('informes');
  return (tipus: string) => {
    if (tipus.startsWith('MISSATGE_')) {
      const sub = tipus.slice(9);
      return t('events.MISSATGE', { tipus: t(`tipusMissatge.${sub}`, { defaultValue: sub }) });
    }
    if (tipus.startsWith('ACCIO_')) {
      const sub = tipus.slice(6);
      return t(`tipusAccio.${sub}`, { defaultValue: t('events.ACCIO', { tipus: sub }) });
    }
    return t(`events.${tipus}`, { defaultValue: tipus });
  };
}

/** Color del punt de la cronologia (mockups `.tl-item--*`) segons el tipus d'esdeveniment. */
export function colorEvent(tipus: string): string {
  if (tipus === 'ACTIVACIO_CREADA' || tipus === 'ACCIO_CREADA') return 'red';
  if (tipus.includes('CONFIRM') || tipus.includes('ACCEPTA') || tipus.includes('TANCA')) {
    return 'green';
  }
  if (tipus.startsWith('ESCALA') || tipus === 'ACCIO_EXPERT_ACTIVAT') return 'purple';
  if (tipus.includes('LLIURAT') || tipus === 'ACCIO_ITCAU_VINCULAT') return 'orange';
  if (tipus.startsWith('MISSATGE_RISC')) return 'red';
  return 'blue';
}

/** Color de cada tipus d'activació a la distribució del W-40. */
export function colorTipusActivacio(clau: string): string {
  switch (clau) {
    case 'CRITICA':
      return 'var(--critic)';
    case 'INCIDENCIA':
      return 'var(--incident)';
    case 'AVIS':
      return 'var(--avis)';
    case 'PREVENTIU':
      return 'var(--preventiu)';
    default:
      return 'var(--gray-2)';
  }
}

/** Icona d'una passarel·la d'enviament a partir del seu nom o canal. */
export function iconaPassarella(nom: string): NomIcona {
  const n = nom.toLowerCase();
  if (/(mail|smtp|correu|correo)/.test(n)) return 'envelope';
  if (/(veu|voz|trucada|llamada|twilio|voice|tel)/.test(n)) return 'phone';
  if (/(push|fcm|apns|firebase)/.test(n)) return 'bell';
  return 'server';
}

export function iconaCanal(canal: string): NomIcona {
  if (canal === 'EMAIL') return 'envelope';
  if (canal === 'TRUCADA') return 'phone';
  if (canal === 'PUSH' || canal === 'PUSH_CRITICA') return 'bell';
  return 'paperplane';
}

/** Normalitza un codi d'acció d'auditoria («rol.permisos.desar», «CONFIG_MODIFICAR»). */
function clauAuditoria(codi: string): string {
  return codi.toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

/**
 * Text llegible d'una acció d'auditoria: primer una traducció específica del codi complet i,
 * si no n'hi ha, «verb · entitat» a partir del darrer segment i de l'entitat.
 */
export function useEtiquetaAccioAuditoria() {
  const { t } = useTranslation('informes');
  return (accio: string, entitat?: string | null) => {
    const especifica = t(`auditoriaAccions.${clauAuditoria(accio)}`, { defaultValue: '' });
    if (especifica) return especifica;
    const segments = accio.split(/[._]/).filter(Boolean);
    const verb = clauAuditoria(segments.at(-1) ?? accio);
    const textVerb = t(`auditoriaVerbs.${verb}`, { defaultValue: '' });
    const textEntitat = nomEntitat(t, entitat ?? segments[0] ?? '');
    return textVerb ? `${textVerb} · ${textEntitat}` : accio;
  };
}

export function useEtiquetaEntitatAuditoria() {
  const { t } = useTranslation('informes');
  return (entitat: string) => nomEntitat(t, entitat);
}

function nomEntitat(t: (k: string, o?: Record<string, unknown>) => string, entitat: string) {
  return t(`auditoriaEntitats.${clauAuditoria(entitat)}`, { defaultValue: entitat });
}

export function useEtiquetaIndicador() {
  const { t } = useTranslation('informes');
  return (indicador: string) => {
    const escalat = /^ESCALAT_NIVELL_(\d+)$/.exec(indicador);
    if (escalat) return t('indicadors.ESCALAT_NIVELL', { n: escalat[1] });
    return t(`indicadors.${indicador}`, { defaultValue: indicador });
  };
}

export function badgeEvent(tipus: string): string {
  if (tipus.includes('CONFIRMAT') || tipus === 'ACTIVACIO_TANCADA') return 'ok';
  if (tipus.startsWith('ESCALAT')) return 'critic';
  if (tipus.startsWith('MISSATGE')) return 'crisi';
  if (tipus.startsWith('ACCIO')) return 'avis';
  if (tipus === 'ACTIVACIO_CREADA') return 'incident';
  return 'gray';
}

export interface PuntSerie {
  data: string;
  total: number;
}

/**
 * Agrupa la sèrie diària: per dies si el període és una setmana i per setmanes (blocs de 7
 * dies des del principi del període) en la resta.
 */
export function agrupaSerie(serie: PuntSerie[], periode: Periode): PuntSerie[][] {
  const ordenada = [...serie].sort((a, b) => a.data.localeCompare(b.data));
  if (periode === 'SETMANA') return ordenada.map((p) => [p]);
  const grups: PuntSerie[][] = [];
  ordenada.forEach((p, i) => {
    const g = Math.floor(i / 7);
    (grups[g] ??= []).push(p);
  });
  return grups;
}

export function tendencia(actual: number, anterior: number): number | null {
  if (anterior <= 0) return null;
  return Math.round(((actual - anterior) / anterior) * 100);
}
